import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useRestaurantCart,
  usePaymentConfig,
  RestaurantCheckout,
} from '@happyspace/sdk/restaurant';
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight, Info } from 'lucide-react';
import { SITE, money } from '../lib/site';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Slide-over cart. Lines come from the SDK cart (shared with the menu page);
 * "Checkout" hands off to the SDK's <RestaurantCheckout /> modal, which owns
 * the customer form, Stripe payment element and order submission.
 */
export default function CartDrawer({ isOpen, onClose }: Props) {
  const { items, itemCount, subtotal, tax, total, updateQuantity, removeItem, clearCart } =
    useRestaurantCart();
  const { paymentEnabled, payAtPickupEnabled, loading: payLoading } = usePaymentConfig();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Card payments require completed Stripe onboarding; this business is still
  // 'pending', so surface pay-at-pickup rather than a dead card form.
  const canCheckout = !payLoading && (paymentEnabled || payAtPickupEnabled);

  const drawer = (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/50"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[61] w-full sm:w-[420px] bg-white flex flex-col shadow-2xl"
            role="dialog"
            aria-label="Your order"
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4 shrink-0"
              style={{ background: 'var(--color-ink)' }}
            >
              <div className="flex items-center gap-2.5">
                <ShoppingBag size={18} style={{ color: 'var(--color-yellow)' }} />
                <h2 className="display text-lg text-white">
                  Your Order {itemCount > 0 && <span className="text-white/50">({itemCount})</span>}
                </h2>
              </div>
              <button
                onClick={onClose}
                aria-label="Close cart"
                className="w-9 h-9 rounded-md flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Lines */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-16">
                  <span
                    className="w-16 h-16 rounded-full border-2 flex items-center justify-center"
                    style={{ borderColor: 'var(--color-line)' }}
                  >
                    <ShoppingBag size={24} className="text-ink-2" />
                  </span>
                  <p className="font-bold uppercase tracking-wide text-sm text-ink">
                    Your cart is empty
                  </p>
                  <p className="text-[13px] text-ink-2 max-w-[220px]">
                    Add something from the menu and it will show up here.
                  </p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {items.map((line) => (
                    <li
                      key={line.cartItemKey}
                      className="flex gap-3 rounded-lg border p-3"
                      style={{ borderColor: 'var(--color-line)' }}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-extrabold uppercase tracking-wide text-ink leading-snug">
                          {line.title}
                        </p>
                        <p className="mt-0.5 text-[12px] text-ink-2">{money(line.basePrice)} each</p>

                        <div className="mt-2.5 flex items-center gap-2">
                          <div
                            className="inline-flex items-center rounded-md border"
                            style={{ borderColor: 'var(--color-line)' }}
                          >
                            <button
                              aria-label={`Decrease ${line.title}`}
                              onClick={() => updateQuantity(line.cartItemKey, line.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center text-ink hover:bg-cream transition-colors"
                            >
                              <Minus size={13} />
                            </button>
                            <span className="w-8 text-center text-[13px] font-bold">{line.quantity}</span>
                            <button
                              aria-label={`Increase ${line.title}`}
                              onClick={() => updateQuantity(line.cartItemKey, line.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center text-ink hover:bg-cream transition-colors"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                          <button
                            aria-label={`Remove ${line.title}`}
                            onClick={() => removeItem(line.cartItemKey)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-ink-2 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                      <p className="display text-lg shrink-0" style={{ color: 'var(--color-ink)' }}>
                        {money(line.itemTotal)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Totals + checkout */}
            {items.length > 0 && (
              <div className="shrink-0 border-t px-5 py-4" style={{ borderColor: 'var(--color-line)' }}>
                <dl className="space-y-1.5 text-[13px]">
                  <div className="flex justify-between text-ink-2">
                    <dt>Subtotal</dt>
                    <dd>{money(subtotal)}</dd>
                  </div>
                  <div className="flex justify-between text-ink-2">
                    <dt>Tax</dt>
                    <dd>{money(tax)}</dd>
                  </div>
                  <div
                    className="flex justify-between pt-2 mt-2 border-t text-ink"
                    style={{ borderColor: 'var(--color-line)' }}
                  >
                    <dt className="display text-lg">Total</dt>
                    <dd className="display text-lg" style={{ color: 'var(--color-ink)' }}>
                      {money(total)}
                    </dd>
                  </div>
                </dl>

                {!payLoading && !canCheckout && (
                  <p
                    className="mt-3 flex items-start gap-2 rounded-md p-2.5 text-[12px]"
                    style={{ background: 'var(--color-cream)', color: 'var(--color-ink-2)' }}
                  >
                    <Info size={14} className="mt-px shrink-0" />
                    Online ordering isn&apos;t switched on for this location yet, please call to
                    place your order.
                  </p>
                )}

                <button
                  onClick={() => setCheckoutOpen(true)}
                  disabled={!canCheckout}
                  className="btn-yellow mt-4 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Checkout <ArrowRight size={15} />
                </button>
                <button
                  onClick={clearCart}
                  className="mt-2 w-full text-[12px] font-semibold uppercase tracking-wide text-ink-2 hover:text-ink transition-colors py-2"
                >
                  Clear cart
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {createPortal(drawer, document.body)}
      <RestaurantCheckout
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        accentColor="#4c9a2a"
        restaurantName={SITE.name}
        taxRate={SITE.taxRate}
        onOrderSuccess={() => {
          setCheckoutOpen(false);
          onClose();
        }}
      />
    </>
  );
}
